// Bulk run modal. Lists every selected record by name, shows who will run and who is
// skipped (and why), runs them with live per-row status, and keeps the list up as a
// record when finished. Used for contact enrichment and company signal scans.
import { useState, useRef } from 'react';

const STATE_LABEL = { willrun: 'Will run', queued: 'Queued', running: 'Running', done: 'Done', failed: 'Failed', skipped: 'Skipped', notrun: 'Not run' };

export default function BulkRunModal({ title, verb, skipNote, forceLabel, items: initialItems, runOne, concurrency = 1, gapMs = 0, onClose }) {
  const [items] = useState(() => initialItems || []);
  const [forceAll, setForceAll] = useState(false);
  const [phase, setPhase] = useState('review');
  const [status, setStatus] = useState({});
  const [stopping, setStopping] = useState(false);
  const stopRef = useRef(false);

  const toRun = forceAll ? items : items.filter(i => i.due);
  const runIds = new Set(toRun.map(i => i.id));
  const skipped = items.length - toRun.length;
  const ordered = [...items].sort((a, b) => (runIds.has(a.id) === runIds.has(b.id) ? 0 : (runIds.has(a.id) ? -1 : 1)));
  const vals = Object.values(status);
  const doneCount = vals.filter(s => s.state === 'done').length;
  const failedCount = vals.filter(s => s.state === 'failed').length;
  const creditsOut = vals.some(s => s.creditsOut);
  const finished = doneCount + failedCount;

  const setOne = (id, v) => setStatus(prev => ({ ...prev, [id]: v }));

  async function start() {
    if (!toRun.length) return;
    stopRef.current = false;
    const init = {};
    for (const it of items) init[it.id] = runIds.has(it.id) ? { state: 'queued' } : { state: 'skipped' };
    setStatus(init);
    setPhase('running');
    const queue = toRun.map(i => i.id);
    const flags = { creditsOut: false };
    for (let i = 0; i < queue.length; i += concurrency) {
      if (stopRef.current) break;
      const group = queue.slice(i, i + concurrency);
      group.forEach(id => setOne(id, { state: 'running' }));
      await Promise.all(group.map(async id => {
        let r;
        try { r = await runOne(id); } catch (e) { r = { ok: false }; }
        if (r && r.creditsOut) {
          flags.creditsOut = true;
          stopRef.current = true;
          setOne(id, { state: 'notrun', label: 'Out of credits', creditsOut: true });
          return;
        }
        setOne(id, r && r.ok ? { state: 'done', label: r.label || 'Done' } : { state: 'failed', label: (r && r.label) || 'Failed' });
      }));
      if (gapMs && i + concurrency < queue.length && !stopRef.current) await new Promise(res => setTimeout(res, gapMs));
    }
    setStatus(prev => {
      const next = { ...prev };
      for (const id of queue) {
        if (next[id] && (next[id].state === 'queued' || next[id].state === 'running')) {
          next[id] = { state: 'notrun', label: flags.creditsOut ? 'Not run, out of credits' : 'Not run, stopped' };
        }
      }
      return next;
    });
    setPhase('done');
    setStopping(false);
  }

  const rowState = (it) => {
    if (phase === 'review') return runIds.has(it.id) ? { state: 'willrun', label: `Will ${verb.toLowerCase()}` } : { state: 'skipped' };
    return status[it.id] || { state: 'skipped' };
  };
  const close = () => { if (phase === 'running') return; onClose(phase === 'done'); };

  return (
    <div className="crmsq3-ov" onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="crmsq3-modal crmbr-modal">
        <div className="crmsq3-modal-h">
          <h3>{title}</h3>
          <button type="button" className="crmsq3-modal-x" aria-label="Close" onClick={close}>{'\u00D7'}</button>
        </div>
        <div className="crmsq3-modal-b">
          <div className="crmbr-sum">
            <span className="crmbr-tag crmbr-tag--run">{toRun.length} will run</span>
            {skipped > 0 && <span className="crmbr-tag">{skipped} {skipNote}</span>}
            {phase !== 'review' && <span className="crmbr-tag crmbr-tag--done">{doneCount} done</span>}
            {phase !== 'review' && failedCount > 0 && <span className="crmbr-tag crmbr-tag--fail">{failedCount} failed</span>}
          </div>
          {phase === 'review' && forceLabel && skipped > 0 && (
            <label className="crmbr-force">
              <input type="checkbox" className="crmsq3-check" checked={forceAll} onChange={e => setForceAll(e.target.checked)} />
              {forceLabel} ({items.length})
            </label>
          )}
          {creditsOut && <p className="crmbr-alert">Out of credits. The rest were not run.</p>}
          <div className="crmbr-list">
            {ordered.map(it => {
              const st = rowState(it);
              const sub = [it.sub, it.reason].filter(Boolean).join(' \u00B7 ');
              return (
                <div key={it.id} className="crmbr-row">
                  <div className="crmbr-row-main">
                    <span className="crmbr-name">{it.name}</span>
                    {sub && <span className="crmbr-sub">{sub}</span>}
                  </div>
                  <span className={'crmbr-state crmbr-state--' + st.state}>{st.label || STATE_LABEL[st.state]}</span>
                </div>
              );
            })}
          </div>
          {phase !== 'review' && (
            <div className="crmsq3-prog crmbr-prog">
              <div className="crmsq3-prog-bar"><div className="crmsq3-prog-fill" style={{ width: (toRun.length ? Math.round(finished / toRun.length * 100) : 0) + '%' }} /></div>
              <span className="crmsq3-prog-txt">{finished} of {toRun.length}</span>
            </div>
          )}
        </div>
        <div className="crmsq3-modal-f">
          {phase === 'review' && (<>
            <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={close}>Cancel</button>
            <button type="button" className="crmsq3-btn" onClick={start} disabled={!toRun.length}>{toRun.length ? `${verb} ${toRun.length}` : `Nothing to ${verb.toLowerCase()}`}</button>
          </>)}
          {phase === 'running' && (
            <button type="button" className="crmsq3-btn crmsq3-btn--danger" onClick={() => { stopRef.current = true; setStopping(true); }} disabled={stopping}>{stopping ? 'Stopping\u2026' : 'Stop'}</button>
          )}
          {phase === 'done' && <button type="button" className="crmsq3-btn" onClick={close}>Close</button>}
        </div>
      </div>
    </div>
  );
}
